import { isEmpty } from 'lodash';
import { createSelector } from 'reselect';
import { getUserIdSelector } from '../user/selectors';

const DEFAULT_OBJECT = {};
const DEFAULT_ARRAY = [];

const getWorkspaceReducer = (state) =>
  state?.workspaceReducer || DEFAULT_OBJECT;

const getCurrentWorkspace = createSelector(
  getWorkspaceReducer,
  (workspaceState) => {
    const result = workspaceState?.currentWorkspace;
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getCurrentWorkspaceProjects = createSelector(
  getCurrentWorkspace,
  (currentWorkspace) => {
    const result = currentWorkspace?.projects;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getWorkspaceCustomFields = createSelector(
  getWorkspaceReducer,
  (workspaceState) => {
    const result = workspaceState?.workspaceCustomFields;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

// CFs = CustomFields
export const getProjectsCFsMatchingWorkspaceCFs = createSelector(
  [getCurrentWorkspaceProjects, getWorkspaceCustomFields],
  (projects, workspaceCFs) => {
    const result = [];

    workspaceCFs.forEach((workspaceCF) => {
      const wCFName = workspaceCF?.name;

      projects.forEach((project) => {
        const pCF = (project.customFields || []).find(
          (pCF) => pCF?.name === wCFName,
        );

        if (!isEmpty(pCF)) {
          result.push({
            _id: pCF?._id,
            name: pCF?.name,
            projectLabel: project?.title,
            projectId: project?._id,
          });
        }
      });
    });

    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

const getProjectIdProp = (state, props) => props.projectId;

const getWorkspaceProjectById = createSelector(
  [getCurrentWorkspaceProjects, getProjectIdProp],
  (projects, projectId) => {
    const result = projects.find((project) => project?._id === projectId);
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const getWorkspaceProjectCustomFields = createSelector(
  getWorkspaceProjectById,
  (project) => {
    const result = project?.customFields;
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const isWorkspaceProjectCustomFieldsLoaded = createSelector(
  getWorkspaceProjectById,
  (project) => {
    const result = project?.customFields;
    return result != null;
  },
);

const getCustomFieldIdProp = (state, props) => props.customFieldId;
const getCustomFieldValueIdProp = (state, props) => props.customFieldValueId;

const getWorkspaceProjectCustomFieldValueById = createSelector(
  [
    getWorkspaceProjectCustomFields,
    getCustomFieldIdProp,
    getCustomFieldValueIdProp,
  ],
  (workspaceProjectCFs, CFId, CFValueId) => {
    const customField = workspaceProjectCFs.find(
      (workspaceProjectCF) => workspaceProjectCF?._id === CFId,
    );
    const result = customField?.possibleValues?.find(
      (possibleValue) => possibleValue?._id === CFValueId,
    );
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const makeGetWorkspaceProjectCustomFieldValueById = () =>
  getWorkspaceProjectCustomFieldValueById;

const getWorkspaceCustomFieldValueById = createSelector(
  [getWorkspaceCustomFields, getCustomFieldIdProp, getCustomFieldValueIdProp],
  (workspaceCFs, CFId, CFValueId) => {
    const customField = workspaceCFs.find(
      (workspaceCF) => workspaceCF?._id === CFId,
    );
    const result = customField?.possibleValues?.find(
      (possibleValue) => possibleValue?._id === CFValueId,
    );
    return isEmpty(result) ? DEFAULT_OBJECT : result;
  },
);

export const makeGetWorkspaceCustomFieldValueById = () =>
  getWorkspaceCustomFieldValueById;

export const getAllProjectsCustomFields = createSelector(
  getWorkspaceReducer,
  (workspaceState) => {
    const currentWorkspace = workspaceState?.currentWorkspace || DEFAULT_OBJECT;
    const myProjects = currentWorkspace?.myProjects || DEFAULT_ARRAY;
    const workspaceCustomFieldsIdList = (workspaceState?.workspaceCustomFields || DEFAULT_ARRAY).map(field => field?._id)

    const result = myProjects?.reduce((res, project) => {
      const filteredProjectFields = (
        project?.customFields || DEFAULT_ARRAY
      )?.filter((field) => !workspaceCustomFieldsIdList?.includes(field?._id));

      if (isEmpty(filteredProjectFields)) {
        return res;
      }

      return [
        ...res,
        {
          projectId: project?._id,
          projectName: project?.title,
          customFields: (filteredProjectFields || []).map((customField) => ({
            customFieldId: customField?._id,
            customFieldName: customField?.name,
          })),
        },
      ];
    }, []);

    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const getWorkspaceCustomFieldsWithlabels = createSelector(
  getWorkspaceCustomFields,
  (workspaceCustomFields) => {
    const result = (workspaceCustomFields || [])?.map((customField) => ({
      customFieldId: customField?._id,
      customFieldName: customField?.name,
    }));
    return isEmpty(result) ? DEFAULT_ARRAY : result;
  },
);

export const isWorkspaceAdminOrOwnerSelector = createSelector(
  [getCurrentWorkspace, getUserIdSelector],
  (currentWorkspace, userId) => {
    const workspaceMembers = currentWorkspace?.members || [];
    const workspaceUser = workspaceMembers?.find(m => m?._id?.toString() === userId?.toString());
    return ['admin', 'owner'].includes(workspaceUser?.role);
  }
);
